import { ChartOptions } from 'plume-ui';
import { ComposedChartDataItem } from 'plume-ui/dist/utils/charts/chartTypes';
import i18n from '../../utils/i18n';
import { HeroStatType } from '../HealthSummary/services/heroStatsProc';
import { subscriberStatFixtures } from './components/MonitorInfo/fixtures';

import {
  MonitorData,
  MonitorHourlyFreqData,
  MonitorOutreachHourlyStat,
  OutreachInsightsTogglerItem,
  OutreachStatCounts,
} from './types';
import {
  SHORT_DATE_FORMAT,
  SHORT_DATE_TIME_FORMAT,
  STANDARD_TIME_FORMAT,
} from '../../config';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

export const getMonitorLegend = () => [
  {
    label: i18n.t('monitors.monitorLegendItems.locations'),
    color: '#B17395',
    stroke: '#B17395',
  },
];

export const getMonitorLegendWithOutreach = () => [
  {
    label: i18n.t('monitors.monitorLegendItems.locations'),
    color: '#B17395',
    stroke: '#B17395',
  },
  {
    label: i18n.t('monitors.monitorLegendItems.pushNotifsRead'),
    color: '#6269FF',
    stroke: '#6269FF',
  },
  {
    label: i18n.t('monitors.monitorLegendItems.pushNotifsUnread'),
    color: '#757CFF',
    stroke: '#757CFF',
  },
  {
    label: i18n.t('monitors.monitorLegendItems.emailsSent'),
    color: '#17E3AE',
    stroke: '#17E3AE',
  },
  {
    label: i18n.t('monitors.monitorLegendItems.webhook'),
    color: '#0FDCFF',
    stroke: '#0FDCFF',
  },
];

export const getAffectedLocationsChartLegend = () => [
  {
    label: i18n.t('monitors.affectedLocationsLegend.newAffectedSubs'),
    color: '#999eff',
  },
  {
    label: i18n.t('monitors.affectedLocationsLegend.totalAffected'),
    color: '#58eec7',
  },
];

const monitorChartGroupsBasic = [
  {
    type: ChartOptions.ChartType.LINE,
    strokeWidth: 2,
  },
];

const affectedLocationsGroups = [
  {
    type: ChartOptions.ChartType.LINE,
    strokeWidth: 2,
  },
  {
    type: ChartOptions.ChartType.LINE,
    strokeWidth: 2,
  },
];

const monitorChartGroups = [
  ...monitorChartGroupsBasic,
  {
    type: ChartOptions.ChartType.BAR,
  },
  {
    type: ChartOptions.ChartType.BAR,
  },
  {
    type: ChartOptions.ChartType.BAR,
  },
  {
    type: ChartOptions.ChartType.BAR,
  },
];

export const getMonitorChartGroups = (
  length: number = 5,
): ChartOptions.ComposedChartGroup[] => {
  if (length === 1) length = 2;
  const monitorLegend = getMonitorLegendWithOutreach();
  return monitorChartGroups.slice(0, length).map((group, index) => {
    return {
      ...group,
      label: monitorLegend[index].label,
      color: monitorLegend[index].color,
      stroke: monitorLegend[index].color,
      key: `key-${monitorLegend[index].label}`,
    };
  });
};

export const getAffectedLocationsChartGroups = (): ChartOptions.ComposedChartGroup[] => {
  return affectedLocationsGroups.map((group, index) => {
    const affectedLocationsChartLegend = getAffectedLocationsChartLegend();
    return {
      ...group,
      label: affectedLocationsChartLegend[index].label,
      color: affectedLocationsChartLegend[index].color,
      stroke: affectedLocationsChartLegend[index].color,
      key: `key-${affectedLocationsChartLegend[index].label}`,
    };
  });
};

export const formatGraphTicks = (
  value: any,
  index: number,
  dataLength: number,
): string => {
  if (
    index === 0 ||
    index === dataLength - 1 ||
    index === Math.round(dataLength / 2)
  )
    return `${value}`;
  return '';
};

export const clampMonitorData = (
  data: MonitorData[],
  timeRange: number,
): MonitorData[] => {
  if (!Array.isArray(data) || !data.length || isNaN(timeRange) || timeRange < 1)
    return [];
  if (timeRange % 1 !== 0) timeRange = Math.round(timeRange);
  if (timeRange <= 3) {
    const hours = timeRange * 24;
    const clampedData =
      data.length > hours ? data.slice(data.length - hours, data.length) : data;
    return clampedData;
  }

  const clampedData =
    data.length > timeRange
      ? data.slice(data.length - timeRange, data.length)
      : data;
  return clampedData;
};

export const parseMonitorDataWithOutreach = (
  data: MonitorData[],
  emailsData: OutreachStatCounts[],
): ComposedChartDataItem[] => {
  const monitorData = deduplicateArray<MonitorData>(data);
  return monitorData.map((point) => {
    const label = point.ts;
    const outreachStats = emailsData.find(
      (dataPoint) => dataPoint.ts === label,
    );
    let result = {
      name: label,
      values: [point.location_count],
    };

    [
      outreachStats?.pushReadCount || 0,
      outreachStats?.pushUnreadCount || 0,
      outreachStats?.emailsSent || 0,
      outreachStats?.webhookCount || 0,
    ].forEach((value) => result.values.push(value));

    return result;
  });
};

export const parseMonitorDataWithOutreachNew = (
  data: MonitorHourlyFreqData[],
  emailsData: MonitorOutreachHourlyStat[],
): ComposedChartDataItem[] => {
  // time difference between each data point
  const timeDiff =
    data[0] && data[1]
      ? Math.abs(dayjs(data[0].ts).diff(dayjs(data[1].ts), 'hour'))
      : 24;
  return data.map((point) => {
    const label = dayjs(point.ts).format(
      timeDiff >= 24 ? SHORT_DATE_FORMAT : SHORT_DATE_TIME_FORMAT,
    );
    const outreachStats = emailsData.find(
      (dataPoint) => dataPoint.ts === point.ts,
    );

    let result = {
      name: label,
      values: [point.location_count],
    };

    [
      outreachStats?.subscriber_open_count || 0,
      outreachStats?.subscriber_bounce_count || 0,
      outreachStats?.support_start_count || 0,
      outreachStats?.webhook_count || 0,
    ].forEach((value) => result.values.push(value));

    return result;
  });
};

export const parseMonitorData = (
  data: MonitorHourlyFreqData[],
  frequencyInHours: number,
): ComposedChartDataItem[] => {
  const format = {
    0: STANDARD_TIME_FORMAT,
    1: STANDARD_TIME_FORMAT,
    3: `${STANDARD_TIME_FORMAT} ${SHORT_DATE_FORMAT}`,
    24: SHORT_DATE_FORMAT,
  }[frequencyInHours];

  return data.map((point) => {
    const label = dayjs(point.ts)
      .utc()
      .local()
      .format(format || STANDARD_TIME_FORMAT);

    let result = {
      name: label,
      values: [point.location_count],
    };

    return result;
  });
};

export function deduplicateArray<T extends any>(array: T[]) {
  return array.filter((value: T, index: number) => {
    const jsonValue = JSON.stringify(value);
    return (
      index ===
      array.findIndex((element: T) => {
        return JSON.stringify(element) === jsonValue;
      })
    );
  });
}

export const getSum = (arr: (number | undefined)[]): number => {
  return arr.reduce(
    (acc: number, val: number | undefined) => (val ?? 0) + acc,
    0,
  );
};

export const parseSubscriberStatsData = (
  data: OutreachStatCounts[],
): number[] => {
  const sendCounts = data.map((point) => point.sendCount);
  const receivedCounts = data.map((point) => point.receivedCount);
  const clickedCounts = data.map((point) => point.clickedCount);
  const blockedCounts = data.map((point) => point.blockedCount);
  const errorCounts = data.map((point) => point.errorCount);

  const sendCountsSum = getSum(sendCounts);
  const receivedCountsSum = getSum(receivedCounts);
  const clickedCountsSum = getSum(clickedCounts);
  const blockedCountsSum = getSum(blockedCounts);
  const errorCountsSum = getSum(errorCounts);

  return [
    sendCountsSum,
    receivedCountsSum,
    clickedCountsSum,
    blockedCountsSum + errorCountsSum,
  ];
};

export function combineSubscriberStats(
  values: number[],
  fixtures: typeof subscriberStatFixtures,
): HeroStatType[] {
  return fixtures.map(
    (fixture: typeof subscriberStatFixtures[0], index: number) => ({
      key: index,
      label: fixture.label,
      value: values[index],
      info: fixture.info,
    }),
  );
}

export const OutreachInsightsTogglerItems: (
  t: (a: string) => string,
) => OutreachInsightsTogglerItem[] = (t) => [
  {
    key: 'on',
    label: t('genericLabels.on'),
  },
  {
    key: 'off',
    label: t('genericLabels.off'),
  },
];

export const getMonitorCellGraphRange = (frequencyInHours: number) => {
  const startDate = (() => {
    const roundedDate = dayjs().minute(0).second(0).millisecond(0);
    if (frequencyInHours >= 24) {
      return roundedDate.subtract(7, 'day').utc().toISOString();
    } else {
      if (frequencyInHours === 0 || frequencyInHours === 1) {
        return roundedDate.subtract(23, 'hours').utc().toISOString();
      } else {
        return roundedDate.subtract(72, 'hours').utc().toISOString();
      }
    }
  })();

  const endDate = dayjs()
    .minute(0)
    .second(0)
    .millisecond(0)
    .utc()
    .toISOString();

  return { startDate, endDate };
};
